/*
 * Coupon Price Sync
 * Mirrors the Snap Coupon discount onto every visible product price.
 * - Hooks the Apply button click on the Snap Coupon UI (.coupon__btn)
 * - Parses the discount % from the coupon subText ("Save 30% on ...")
 * - Rewrites every .price element to show discounted + struck-through original
 * - Persists in sessionStorage so it survives page navigation
 * - Re-applies on DOM mutations (variant change, quick add, infinite scroll)
 *
 * NOTE: purely a visual indicator. The actual cart discount is still applied
 * by the Snap Coupon app via discount code on cart/checkout.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'arki:coupon-discount';
  var APPLIED_CLASS = 'price--coupon-applied';

  // Only these products receive the visual discount.
  // Keep in sync with the discount-code rules configured in the Snap Coupon app.
  var ELIGIBLE_PRODUCT_IDS = [
    '9193193275643',
    '9193193177339',
    '9193181643003',
    '15439043821819',
    '9193193636091'
  ];

  function isEligible(priceEl) {
    var pid = priceEl.getAttribute('data-product-id');
    if (!pid) return false;
    return ELIGIBLE_PRODUCT_IDS.indexOf(String(pid)) !== -1;
  }

  function getMoneyFormat() {
    if (window.theme && window.theme.settings) {
      return window.theme.settings.currencyCodeEnabled
        ? window.theme.settings.moneyWithCurrencyFormat
        : window.theme.settings.moneyFormat;
    }
    return '${{amount}}';
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
      return window.theme.Currency.formatMoney(cents, getMoneyFormat());
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function parseDiscountFromCoupon(couponEl) {
    if (!couponEl) return null;

    var subText = couponEl.querySelector('.coupon__subText');
    var headText = couponEl.querySelector('.coupon__headText');
    var text = ((subText ? subText.textContent : '') + ' ' + (headText ? headText.textContent : '')).trim();
    if (!text) return null;

    var label = (headText && headText.textContent.trim()) || 'Discount';

    var pctMatch = text.match(/(\d+(?:\.\d+)?)\s*%/);
    if (pctMatch) {
      return { type: 'percent', value: parseFloat(pctMatch[1]), label: label };
    }
    var dollarMatch = text.match(/\$\s*(\d+(?:[.,]\d+)?)/);
    if (dollarMatch) {
      var amount = parseFloat(dollarMatch[1].replace(',', '.'));
      return { type: 'fixed', value: Math.round(amount * 100), label: label };
    }
    return null;
  }

  function computeDiscounted(originalCents, discount) {
    var out;
    if (discount.type === 'percent') {
      out = Math.round(originalCents * (1 - discount.value / 100));
    } else {
      out = originalCents - discount.value;
    }
    return Math.max(0, out);
  }

  function readPriceCents(priceEl) {
    var raw = priceEl.getAttribute('data-product-price') || priceEl.getAttribute('data-price');
    if (!raw) return NaN;
    return parseInt(raw, 10);
  }

  function applyToPriceEl(priceEl, discount) {
    if (!isEligible(priceEl)) return;
    var original = readPriceCents(priceEl);
    if (isNaN(original) || original <= 0) return;

    var discounted = computeDiscounted(original, discount);
    var regularEl = priceEl.querySelector('.price__regular');

    if (regularEl) {
      // Shape A: .price wrapper containing .price__regular (and optional .price__sale)
      if (!priceEl.dataset.originalRegularHtml) {
        priceEl.dataset.originalRegularHtml = regularEl.innerHTML;
      }
      var existingSale = priceEl.querySelector('.price__sale');
      if (existingSale && !priceEl.dataset.originalSaleHtml) {
        priceEl.dataset.originalSaleHtml = existingSale.innerHTML;
      }

      var prefix = '';
      if (priceEl.getAttribute('data-price-varies') === 'true') {
        var fromMatch = priceEl.dataset.originalRegularHtml.match(/^[^<]*?(\D+)\d/);
        if (fromMatch) prefix = fromMatch[1];
      }
      regularEl.innerHTML = (prefix || '') + formatMoney(discounted);

      var saleEl = existingSale;
      if (!saleEl) {
        saleEl = document.createElement('span');
        saleEl.className = 'price__sale price__coupon-original inline-flex items-center h-auto relative';
        regularEl.parentNode.insertBefore(saleEl, regularEl.nextSibling);
      }
      saleEl.classList.add('price__coupon-original');
      saleEl.innerHTML = formatMoney(original);
    } else {
      // Shape B: bare element whose own content is the price (e.g. <product-buy-price>)
      if (!priceEl.dataset.originalRegularHtml) {
        priceEl.dataset.originalRegularHtml = priceEl.innerHTML;
      }
      priceEl.innerHTML =
        '<span class="price__regular price__coupon-new">' + formatMoney(discounted) + '</span>' +
        '<span class="price__sale price__coupon-original">' + formatMoney(original) + '</span>';
    }

    priceEl.classList.add(APPLIED_CLASS, 'price--on-sale');
    priceEl.setAttribute('data-coupon-label', discount.label || '');
  }

  function clearPriceEl(priceEl) {
    if (!priceEl.classList.contains(APPLIED_CLASS)) return;

    var hasRegularChild = !!priceEl.querySelector('.price__regular:not(.price__coupon-new)');
    if (hasRegularChild) {
      var regularEl = priceEl.querySelector('.price__regular');
      if (regularEl && priceEl.dataset.originalRegularHtml) {
        regularEl.innerHTML = priceEl.dataset.originalRegularHtml;
      }
      var saleEl = priceEl.querySelector('.price__coupon-original');
      if (saleEl) {
        if (priceEl.dataset.originalSaleHtml) {
          saleEl.classList.remove('price__coupon-original');
          saleEl.innerHTML = priceEl.dataset.originalSaleHtml;
        } else {
          saleEl.parentNode.removeChild(saleEl);
        }
      }
    } else if (priceEl.dataset.originalRegularHtml) {
      priceEl.innerHTML = priceEl.dataset.originalRegularHtml;
    }

    delete priceEl.dataset.originalRegularHtml;
    delete priceEl.dataset.originalSaleHtml;

    if (!priceEl.hasAttribute('data-compare-price')) {
      priceEl.classList.remove('price--on-sale');
    }
    priceEl.classList.remove(APPLIED_CLASS);
    priceEl.removeAttribute('data-coupon-label');
  }

  var PRICE_SELECTOR = '.price[data-product-price], product-buy-price[data-price]';
  var BADGE_SELECTOR = '[data-coupon-badge]';

  // Read the coupon's discount straight from the Snap widget, regardless of
  // whether it's been applied to the cart yet — the widget shows e.g. "Save 30%".
  function getWidgetDiscount() {
    var couponEl = document.querySelector('[data-coupon-id]') ||
                   document.querySelector('#scp__main-container');
    return parseDiscountFromCoupon(couponEl);
  }

  function formatBadge(discount) {
    if (!discount) return '';
    if (discount.type === 'percent') {
      var v = discount.value % 1 === 0 ? discount.value.toFixed(0) : String(discount.value);
      return v + '% Off Sale';
    }
    return formatMoney(discount.value) + ' Off';
  }

  // Upgrade eligible-product badges to the live discount text *if* a real value
  // is available (Snap widget present or a previously-applied discount stored).
  // Never blanks the badge — the server-rendered default stays as the fallback.
  function refreshBadges() {
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    if (!badges.length) return;
    var discount = getWidgetDiscount() || loadDiscount();
    if (!discount) return;
    var text = formatBadge(discount);
    if (!text) return;
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = text;
      badges[i].removeAttribute('hidden');
    }
  }

  function applyToAll(discount) {
    var prices = document.querySelectorAll(PRICE_SELECTOR);
    for (var i = 0; i < prices.length; i++) {
      if (!prices[i].classList.contains(APPLIED_CLASS)) {
        applyToPriceEl(prices[i], discount);
      }
    }
  }

  function clearAll() {
    var prices = document.querySelectorAll('.' + APPLIED_CLASS);
    for (var i = 0; i < prices.length; i++) clearPriceEl(prices[i]);
  }

  function saveDiscount(discount) {
    try {
      if (discount) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(discount));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function loadDiscount() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  // True when the coupon button is in its "applied/active" state.
  // The Snap Coupon app toggles the button text between "Apply" (inactive)
  // and "Applied" (or similar) when the coupon is on the cart.
  function isCouponButtonActive(btn) {
    var text = (btn.textContent || '').trim().toLowerCase();
    if (!text) return false;
    if (text === 'apply') return false;
    if (text === 'applying' || text === 'loading') return false; // transitional
    return text.indexOf('applied') !== -1 ||
           text.indexOf('remove') !== -1 ||
           text.indexOf('active') !== -1;
  }

  function syncFromCouponButton(btn) {
    var couponEl = btn.closest('[data-coupon-id]') || btn.closest('#scp__main-container');
    var current = loadDiscount();
    var active = isCouponButtonActive(btn);

    // Whenever the coupon button is seen/updated, sync the live % onto the badges.
    // Cheap (one querySelectorAll), runs only on button discovery/toggle.
    refreshBadges();

    if (active && !current) {
      var discount = parseDiscountFromCoupon(couponEl);
      if (discount) {
        saveDiscount(discount);
        applyToAll(discount);
        window.dispatchEvent(new CustomEvent('arki:coupon-applied', { detail: discount }));
      }
    } else if (!active && current) {
      saveDiscount(null);
      clearAll();
      window.dispatchEvent(new CustomEvent('arki:coupon-removed'));
    }
  }

  function watchCouponButton(btn) {
    if (btn.__arkiCouponWatched) return;
    btn.__arkiCouponWatched = true;
    syncFromCouponButton(btn);

    var btnObserver = new MutationObserver(function () {
      syncFromCouponButton(btn);
    });
    btnObserver.observe(btn, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-pressed']
    });
  }

  function discoverCouponButtons(root) {
    (root || document).querySelectorAll('.coupon__btn').forEach(watchCouponButton);
  }

  // Immediate response on click as well (in case the app's text update is delayed
  // or replaces the button entirely without re-running the observer setup).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.coupon__btn');
    if (!btn) return;
    watchCouponButton(btn);
    setTimeout(function () { syncFromCouponButton(btn); }, 60);
    setTimeout(function () { syncFromCouponButton(btn); }, 400);
  }, true);

  window.arkiClearCouponDiscount = function () {
    saveDiscount(null);
    clearAll();
  };

  function init() {
    var discount = loadDiscount();
    if (discount) applyToAll(discount);
    refreshBadges();
    discoverCouponButtons();
    startObserver();
  }

  function startObserver() {
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function (mutations) {
      var discount = loadDiscount();

      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];

        // Discovery: always pick up newly-added coupon buttons, even with no active discount
        for (var aj = 0; aj < m.addedNodes.length; aj++) {
          var addedNode = m.addedNodes[aj];
          if (addedNode.nodeType !== 1) continue;
          if (addedNode.matches && addedNode.matches('.coupon__btn')) watchCouponButton(addedNode);
          if (addedNode.querySelectorAll) discoverCouponButtons(addedNode);
        }

        if (!discount) continue;

        // Case A: a previously-applied element was overwritten in place
        // (e.g. <product-buy-price> innerHTML reset on bundle:change)
        if (m.target && m.target.nodeType === 1 &&
            m.target.classList && m.target.classList.contains(APPLIED_CLASS) &&
            m.target.matches && m.target.matches(PRICE_SELECTOR) &&
            !m.target.querySelector('.price__coupon-original')) {
          m.target.classList.remove(APPLIED_CLASS, 'price--on-sale');
          delete m.target.dataset.originalRegularHtml;
          delete m.target.dataset.originalSaleHtml;
          applyToPriceEl(m.target, discount);
          continue;
        }

        // Case B: new price elements added to the DOM (variant change, infinite scroll, etc.)
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(PRICE_SELECTOR) && !node.classList.contains(APPLIED_CLASS)) {
            applyToPriceEl(node, discount);
          }
          if (node.querySelectorAll) {
            var inner = node.querySelectorAll(PRICE_SELECTOR);
            for (var k = 0; k < inner.length; k++) {
              if (!inner[k].classList.contains(APPLIED_CLASS)) applyToPriceEl(inner[k], discount);
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
